/**
 *
 * Loads all commands and exports the UserActions used by the rest of the system.
 *
 */
import {
    AddUserActions, UserCmd, UserActions as cmdResigistry,
    CustomActions, UserAction, AddCustomAction,
} from './UserCmd';
import { ConnectedDataCmds } from './ConnectedDataCmds';
import { DisplayCmds } from './DisplayCmds';
import { SaveRestoreCmds } from './SaveRestoreCmds';
import { ComputationCmds } from './ComputationCmds';
import { FragmentsCmds } from './FragmentsCmds';
import { ModificationCmds } from './ModificationCmds';
import { UICmds } from './UICmds';
import { DesignCmds } from './DesignCmds';
import { ScoringCmds } from './ScoringCmds';
// The medchem commands were built, given ActionDefs, and sanctioned as a boundary seam
// (test/bmaps/gibbsImportBoundary.test.js SEAMS, "nothing here is static") -- but nothing ever
// imported them, so AddUserActions never saw them. Measured on jfreeze 2026-10-16: "scan this
// compound for structural alerts" answered "Scan for Structural Alerts (Toxicophores) is not
// available from chat yet", because `hasDispatchRoute` found no `UserActions.ScanStructuralAlerts`
// and `planCanRun` therefore kept all FIVE medchem actions out of the planner's candidate set.
//
// Safe to import statically: MedchemCmds' only static imports are BMapsApp, UserCmd and
// js_utils. Every gibbs/ module it uses -- the knowledge tree, which is the heavy part -- is
// reached through `await import()`, so this adds no gibbs code to the main bundle.
//
// Registering lazily on first dispatch would NOT fix this: `planCanRun` is evaluated when the
// prompt is built, so an unregistered command is filtered out of the candidate set before any
// dispatch can happen.
import { MedchemCmds } from './MedchemCmds';
// The third-party database lookups (RCSB CCD, UniProt, KLIFS, PubChem). Their ActionDefs used
// to declare `serverCommand`, which cannot reach them -- they are browser `fetch` calls, not
// bfd-server commands -- so each was refused at dispatch. Registering them here puts them on
// the same route as every other reachable action, and under the same callee-contract test.
// Static import is cheap: these modules pull in WebServices and js_utils only.
import { ExternalDataCmds } from './ExternalDataCmds';

const actions = {
    ...ConnectedDataCmds,
    ...DisplayCmds,
    ...SaveRestoreCmds,
    ...ComputationCmds,
    ...FragmentsCmds,
    ...ModificationCmds,
    ...UICmds,
    ...DesignCmds,
    ...ScoringCmds,
    ...MedchemCmds,
    ...ExternalDataCmds,
};

AddUserActions(actions);

/** @type {actions} UserActions */
export const UserActions = cmdResigistry;

export {
    UserCmd,
    AddUserActions,
    CustomActions,
    UserAction,
    AddCustomAction,
};

export { default as CmdInterpreter } from './CmdInterpreter';
